import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, ToggleLeft, ToggleRight, Flame } from "lucide-react";

export default function AwarenessMixes() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [family, setFamily] = useState("all");
  const list = useQuery({
    queryKey: ["awareness-mixes"],
    queryFn: () => api.get("/awareness-mixes").then(r => r.data),
  });
  const toggle = useMutation({
    mutationFn: ({id, enabled}) => api.patch(`/awareness-mixes/${id}`, { enabled }),
    onSuccess: () => qc.invalidateQueries({queryKey:["awareness-mixes"]}),
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const families = useMemo(() => {
    const s = new Set(); (list.data || []).forEach(m => m.family && s.add(m.family));
    return ["all", ...Array.from(s)];
  }, [list.data]);
  const filtered = useMemo(() => (list.data || []).filter(m => {
    if (family !== "all" && m.family !== family) return false;
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (m.family + m.angle + m.vibe + m.psychology).toLowerCase().includes(t);
  }), [list.data, family, q]);

  const enabledCount = (list.data || []).filter(m => m.enabled).length;

  return (
    <Layout title="Awareness Mixes." subtitle={`${list.data?.length ?? 0} persuasion angles · ${enabledCount} enabled`}>
      <div className="mb-6 border border-blue-500/25 bg-blue-500/5 p-4 flex gap-3 items-start" data-testid="mixes-hint">
        <Flame className="w-4 h-4 text-blue-400 shrink-0 mt-0.5"/>
        <div className="text-[12px] text-blue-200 leading-relaxed">
          Every orchestration randomly samples <b>3 enabled mixes</b> and injects them as suggested angles into Jarvis's prompt. Toggle mixes off to remove them from the pool.
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"/>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search vibe / angle / psychology..."
            data-testid="mixes-search"
            className="w-full bg-[var(--input-bg)] border border-[var(--border)] focus:border-[var(--border-strong)] focus:outline-none pl-10 pr-3 py-2 text-[13px]"
          />
        </div>
        <select
          value={family}
          onChange={(e) => setFamily(e.target.value)}
          data-testid="mixes-family-filter"
          className="bg-[var(--input-bg)] border border-[var(--border)] px-3 py-2 text-[12px] font-mono"
        >
          {families.map(f => <option key={f} value={f}>{f === "all" ? "All families" : f}</option>)}
        </select>
      </div>

      {list.isLoading && <div className="text-[var(--text-muted)] p-6 text-sm">Loading…</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map(m => (
          <div
            key={m.id}
            data-testid={`mix-card-${m.mix_id}`}
            className={`border p-4 transition-colors ${m.enabled ? "border-[var(--border)] bg-[var(--surface)]" : "border-[var(--border)] bg-[var(--bg-2)] opacity-50"}`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--accent)]">#{m.mix_id}</span>
                  <span className="font-mono text-[9px] uppercase tracking-widest text-[var(--text-muted)]">{m.family}</span>
                </div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-400 mb-1.5">{m.angle}</div>
                <div className="text-[13px] text-[var(--text-primary)] leading-relaxed italic mb-2">
                  &ldquo;{m.vibe}&rdquo;
                </div>
                {m.psychology && (
                  <div className="text-[11px] text-[var(--text-muted)] leading-relaxed border-l-2 border-[var(--border)] pl-2">
                    <b>Why it works:</b> {m.psychology}
                  </div>
                )}
                {m.performance && (
                  <div className="text-[10px] font-mono text-[var(--text-muted)] mt-2 uppercase tracking-widest">
                    → {m.performance}
                  </div>
                )}
              </div>
              <button
                onClick={() => toggle.mutate({id: m.id, enabled: !m.enabled})}
                data-testid={`mix-toggle-${m.mix_id}`}
                className="shrink-0 p-1.5 border border-[var(--border)] hover:border-[var(--border-strong)] text-[var(--text-secondary)]"
                title={m.enabled ? "Disable" : "Enable"}
              >
                {m.enabled ? <ToggleRight className="w-4 h-4 text-emerald-400"/> : <ToggleLeft className="w-4 h-4"/>}
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && !list.isLoading && (
          <div className="md:col-span-2 border border-dashed border-[var(--border)] p-12 text-center bg-dots">
            <div className="text-[var(--text-muted)] text-sm">No mixes match your search.</div>
          </div>
        )}
      </div>
    </Layout>
  );
}
